import React from "react";
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import { HubIcon } from "../HubIcon";

export const RobotSyncStatus = ({
  dipInstructions,
  showDetails = false,
}) => {
  // Get timestamps from dipInstructions prop
  const recipeUpdateTime = dipInstructions?.recipeUpdateTime ? new Date(dipInstructions.recipeUpdateTime) : null;
  const robotSyncTime = dipInstructions?.robotSyncTime ? new Date(dipInstructions.robotSyncTime) : null;
  
  // If no timestamps are available, don't render anything
  if (!recipeUpdateTime && !robotSyncTime) {
    return null;
  }
  
  // Determine if robots are synced (robot sync is after or equal to recipe update)
  // If robotSyncTime is null, consider it out of sync
  // If recipeUpdateTime is null but robotSyncTime exists, consider it synced
  const isSynced = robotSyncTime && (!recipeUpdateTime || robotSyncTime >= recipeUpdateTime);
  
  // Format timestamps for tooltip
  const formatTimestamp = (date) => {
    if (!date) return 'Not available';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    }).format(date);
  };
  
  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold', mb: 1, color: 'white' }}>
        Robot Sync Status
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '11px', mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
        Last Recipe Update: {formatTimestamp(recipeUpdateTime)}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)' }}>
        Last Robot Sync: {formatTimestamp(robotSyncTime)}
      </Typography>
    </Box>
  );
  
  const statusColor = isSynced ? "#4caf50" : "#ff9800"; // green for synced, orange for out of sync
  const statusLabel = isSynced ? "Synced" : "Out of Sync";
  
  const content = (
    <Stack 
      direction="row" 
      gap="4px" 
      alignItems="center"
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        backgroundColor: isSynced ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 152, 0, 0.08)',
        border: `1px solid ${isSynced ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)'}`,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: statusColor,
        }}
      />
      <Typography sx={{ fontSize: "10px", color: statusColor, fontWeight: 500 }}>
        Robots {statusLabel}
      </Typography>
    </Stack>
  );
  
  if (showDetails) {
    return (
      <Stack spacing={1}>
        <Tooltip title={tooltipContent} arrow placement="top">
          <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
            {content}
          </Box>
        </Tooltip>
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ fontSize: '10px', color: 'text.secondary' }}>
            Recipe: {formatTimestamp(recipeUpdateTime)}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '10px', color: 'text.secondary' }}>
            Robot: {formatTimestamp(robotSyncTime)}
          </Typography>
        </Stack>
      </Stack>
    );
  }
  
  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
        {content}
      </Box>
    </Tooltip>
  );
};

export default RobotSyncStatus;